import { useCallback, useState } from 'react';

import { type MemoryEditPromptResponse } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useMutation } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

export interface UseCreateMemoryEditProps extends AssistanceServiceInjectedParams {
	/**
	 * Called with the created memory edit on success
	 */
	onSuccess?: (data: MemoryEditPromptResponse) => void;
	/**
	 * Called with the error if the mutation fails
	 */
	onError?: (error: ApiError) => void;
}

export interface UseCreateMemoryEditReturn {
	/**
	 * Function to create a new memory edit
	 */
	onCreateMemoryEdit: (content: string) => Promise<MemoryEditPromptResponse | undefined>;
	/**
	 * Whether the mutation is in progress
	 */
	isSubmitting: boolean;
	/**
	 * Whether the most recent mutation failed. Cleared when a new submit starts and
	 * on success.
	 */
	hasError: boolean;
}

/**
 * Hook for creating a memory edit via POST /memories/memory-edits.
 * Accepts optional onSuccess and onError callbacks.
 */
export const useCreateMemoryEdit: (
	props: Omit<UseCreateMemoryEditProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => UseCreateMemoryEditReturn = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams,
		onSuccess,
		onError,
	}: UseCreateMemoryEditProps): UseCreateMemoryEditReturn => {
		const { assistanceService } = assistanceServiceInjectedParams;

		const [hasError, setHasError] = useState(false);

		const { mutate: createMemoryEdit, isSubmitting } = useMutation<
			{ content: string },
			MemoryEditPromptResponse
		>({
			mutationFn: async (payload: { content: string }) => {
				return assistanceService.createMemoryEdit(payload);
			},
			onSuccess: (data) => {
				setHasError(false);
				onSuccess?.(data);
			},
			onError: (error) => {
				setHasError(true);
				onError?.(error);
			},
		});

		const onCreateMemoryEdit = useCallback(
			(content: string) => {
				setHasError(false);
				return createMemoryEdit({ content });
			},
			[createMemoryEdit],
		);

		return {
			onCreateMemoryEdit,
			isSubmitting,
			hasError,
		};
	},
);
